import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/apiClient';
import { projectKeys } from '@/hooks/queries/useProjectsQuery';
import { toast } from 'sonner';
import { RoleType } from '@/types/project';
import type {
  Organization,
  CreateOrganizationCommand,
  UpdateOrganizationCommand,
  DeleteOrganizationCommand,
} from '@/types/organization';

export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (filters?: string) => [...organizationKeys.lists(), { filters }] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: number) => [...organizationKeys.details(), id] as const,
  myRoles: () => [...organizationKeys.all, 'myRoles'] as const,
  projects: (id: number, filters?: string) => [...organizationKeys.all, 'projects', id, { filters }] as const,
  assignableProjects: (id: number, search: string) => [...organizationKeys.all, 'assignableProjects', id, search] as const,
};

/** Debounces a value by the given delay in ms. */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export interface OrganizationListParams {
  name?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

const serializeParams = (params: OrganizationListParams = {}) => JSON.stringify(params);

export const useOrganizationsQuery = (params: OrganizationListParams = {}) => {
  return useQuery({
    queryKey: organizationKeys.list(serializeParams(params)),
    queryFn: () => apiClient.getOrganizations(params),
    gcTime: 5 * 60 * 1000,
  });
};

export const useOrganizationListQuery = () => {
  return useQuery({
    queryKey: organizationKeys.lists(),
    queryFn: () => apiClient.getOrganizations({ page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' }),
    select: (data) => data.items ?? [],
    gcTime: 5 * 60 * 1000,
  });
};

export const useMyOrganizationRolesQuery = () => {
  return useQuery({
    queryKey: organizationKeys.myRoles(),
    queryFn: async () => {
      const items = await apiClient.getMyOrganizationRoles();
      return items.reduce<Record<number, RoleType>>((acc, item) => {
        acc[item.organizationId] = item.roleType;
        return acc;
      }, {});
    },
    gcTime: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOrganizationQuery = (id: number) => {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => apiClient.getOrganization(id),
    enabled: !!id,
    gcTime: 5 * 60 * 1000,
  });
};

export interface OrganizationProjectsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const useOrganizationProjectsQuery = (organizationId: number, params: OrganizationProjectsParams = {}) => {
  return useQuery({
    queryKey: organizationKeys.projects(organizationId, serializeParams(params)),
    queryFn: () => apiClient.getOrganizationProjects(organizationId, params),
    enabled: !!organizationId,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Search for projects the current user may move into this organization. Only enabled while
 * the "add existing project" dialog is open (pass `enabled`). The search term is debounced
 * internally so callers can pass the raw input value.
 */
export const useAssignableProjectsQuery = (organizationId: number, search: string, enabled: boolean) => {
  const debouncedSearch = useDebounce(search.trim(), 300);
  return useQuery({
    queryKey: organizationKeys.assignableProjects(organizationId, debouncedSearch),
    queryFn: () => apiClient.getAssignableProjects(organizationId, debouncedSearch),
    enabled: enabled && !!organizationId,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: CreateOrganizationCommand) => apiClient.createOrganization(command),
    onSuccess: (organization) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organizationKeys.myRoles() });
      queryClient.setQueryData<Organization>(organizationKeys.detail(organization.id), organization);
      toast.success('Organization created successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create organization');
    },
  });
};

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, command }: { id: number; command: UpdateOrganizationCommand }) =>
      apiClient.updateOrganization(id, command),
    onSuccess: (organization, { id }) => {
      if (organization && typeof organization.id === 'number') {
        queryClient.setQueryData<Organization>(organizationKeys.detail(id), organization);
      }
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      toast.success('Organization updated successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update organization');
    },
  });
};

export const useDeleteOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, command }: { id: number; command: DeleteOrganizationCommand }) =>
      apiClient.deleteOrganization(id, command),
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      toast.success('Organization deleted successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete organization');
    },
  });
};

export const useAddProjectToOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, projectId }: { organizationId: number; projectId: number }) =>
      apiClient.addProjectToOrganization(organizationId, projectId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'projects', organizationId] });
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'assignableProjects'] });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project moved to organization');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to move project');
    },
  });
};
